import React, { Component } from 'react';
import { connect } from 'react-redux';
import config from 'config';
import {
  importAccounts,
  importAccountsWithAdding,
} from 'actions/accountsActions';
import { addImport } from 'actions/authActions';
import {
  prepareImportAccounts,
  getDuplicatedNumbersInImportData,
  getDuplicatedNumbersInAccountsAndImportData,
  getFinalDataToAdd,
} from 'utils/stateToState';
import Select from 'components/shared/Select';

const ACCOUNT_UPDATE_MODE = {
  ADD_ACCOUNTS: 'ADD_ACCOUNTS',
  UPDATE_ACCOUNTS: 'UPDATE_ACCOUNTS',
};

class ImportAccountsModal extends Component {
  constructor(props) {
    super(props);
    this.onSheetChange = this.onSheetChange.bind(this);
    this.onMapChange = this.onMapChange.bind(this);
    let accountsColumns = [
      'number',
      'description',
      ...this.props.accounts.periodIds,
    ];
    this.state = {
      accountsUpdateMode: ACCOUNT_UPDATE_MODE.ADD_ACCOUNTS,
      // Initially select first sheet
      sheetIndex: 0,
      // Set initial mapping of sheet columns to account, for each sheet
      // sheetMap[sheetIndex][accountColumn] = selectedOptionIndex
      sheetMap: this.props.data.wsnames.reduce((ob1, wsname, wsi) => {
        ob1[wsi] = accountsColumns.reduce((ob2, ac, i) => {
          const headerRow = this.props.data.wsdata[wsi][0];
          // if there are no enough columns in header row leave unmapped
          if (headerRow) {
            ob2[ac] = i < headerRow.length ? i : -1;
          }
          return ob2;
        }, {});
        return ob1;
      }, {}),
      accountsColumns,
    };
  }

  /*
   * For currently selected mapping checks and returns array of messages
   *    how many rows will be used: how many for update and for add
   *    if there there is no mapping for account number and description
   *    if there are duplicate account number or descriptions
   */
  validation() {
    const { accountsUpdateMode } = this.state;

    const messages = [];
    let isImportButtonDisabled = false;

    const mapping = this.state.sheetMap[this.state.sheetIndex];
    const numCol = mapping.number;
    const desCol = mapping.description;

    if (numCol < 0 && desCol < 0) {
      messages.push(
        'You have to select column for account number or description.'
      );
      isImportButtonDisabled = true;
      return {
        messages,
        isImportButtonDisabled,
        duplicatedAccountNums: [],
      };
    }
    if (numCol < 0 || desCol < 0) {
      messages.push(
        'You should select column for both account number and description.'
      );
    }

    const excelData = this.props.data.wsdata[this.state.sheetIndex].slice(1);
    const importData = this.getExcelDataWithFixedTypes(excelData, mapping);

    let [dataToUpdate, dataToAdd, , dupDes] = prepareImportAccounts(
      this.props.accounts,
      importData,
      mapping
    );
    const duplicatedNumbersInImportData = getDuplicatedNumbersInImportData(
      importData,
      mapping
    );
    const [
      duplicatedAccountNums,
      duplicatedAccountData,
    ] = getDuplicatedNumbersInAccountsAndImportData(
      this.props.accounts,
      importData,
      mapping
    );
    let rows = dataToAdd.length + dataToUpdate.length;

    if (duplicatedNumbersInImportData.length > 0) {
      messages.push(
        `Please remove following duplicated account numbers in the import data: 
          ${duplicatedNumbersInImportData.toString()}.`
      );
      isImportButtonDisabled = true;
    }

    if (duplicatedAccountNums.length > 0) {
      messages.push(
        `These accounts have already existing number,
        but different description in the Excel data (Total: ${
          duplicatedAccountNums.length
        }):
        ${duplicatedAccountNums.toString()}.`
      );
    }

    const finalDataToAdd = getFinalDataToAdd({
      dataToAdd,
      duplicatedAccountData,
    });
    const isAddAccountsMode =
      accountsUpdateMode === ACCOUNT_UPDATE_MODE.ADD_ACCOUNTS;

    const accsToBeUpdated = isAddAccountsMode
      ? dataToUpdate.length
      : rows - finalDataToAdd.length;
    const accsToBeAdded = isAddAccountsMode
      ? dataToAdd.length
      : finalDataToAdd.length;

    messages.push(
      `There ${rows > 1 ? 'are' : 'is'} ${rows} row${
        rows > 1 ? 's' : ''
      } to be imported out of which ${accsToBeUpdated} will be updated and ${accsToBeAdded} will be used to create new accounts.`
    );
    if (dupDes > 0) {
      messages.push(
        `There ${dupDes > 1 ? 'are' : 'is'} ${dupDes} row${
          dupDes > 1 ? 's' : ''
        } with already existing account description.`
      );
    }

    return {
      messages,
      isImportButtonDisabled,
      duplicatedAccountNums,
    };
  }

  onSheetChange(e) {
    this.setState({
      sheetIndex: e.target.selectedIndex,
    });
  }

  onMapChange(e) {
    this.setState({
      sheetMap: {
        ...this.state.sheetMap,
        [this.state.sheetIndex]: {
          ...this.state.sheetMap[this.state.sheetIndex],
          [e.target.id]: parseInt(e.target.value, 10),
        },
      },
    });
  }

  getExcelDataWithFixedTypes = (excelData, mapping) => {
    const newExcelData = [...excelData];
    const numCol = mapping.number;

    newExcelData.forEach((row) => {
      const hasNumCol = !(numCol < 0 || !row[numCol]);
      if (hasNumCol) {
        row[numCol] = row[numCol].toString().trim();
      }
    });

    return newExcelData;
  };

  handleSubmit = (event) => {
    const { accountsUpdateMode, sheetMap } = this.state;
    const { importAccounts, importAccountsWithAdding, data } = this.props;

    const excelData = data.wsdata[this.state.sheetIndex].slice(1);
    const mapping = sheetMap[this.state.sheetIndex];

    const excelDataWithFixedTypes = this.getExcelDataWithFixedTypes(
      excelData,
      mapping
    );

    if (accountsUpdateMode === ACCOUNT_UPDATE_MODE.ADD_ACCOUNTS) {
      importAccountsWithAdding(excelDataWithFixedTypes, mapping);
    } else if (accountsUpdateMode === ACCOUNT_UPDATE_MODE.UPDATE_ACCOUNTS) {
      importAccounts(excelDataWithFixedTypes, mapping);
    }

    if (['staging', 'production'].includes(config.env)) {
      this.props.addImport();
    }
    this.props.onClose();
    event.preventDefault();
  };

  handleInputChange = (event) => {
    const { target } = event;
    const { name } = target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    this.setState({
      [name]: value,
    });
  };

  render() {
    const { accountsUpdateMode } = this.state;
    const worksheets = this.props.data.wsnames.map((name, i) => (
      <option key={i}>{name}</option>
    ));
    const sheetMap = this.state.sheetMap[this.state.sheetIndex];
    const mapSelectors = this.state.accountsColumns.map((accCol, i) => {
      return (
        <tr key={i}>
          <td>
            {i < 2 ? accCol : this.props.accounts.periodsById[accCol].name}:
          </td>
          <td>
            <Select
              id={accCol}
              options={this.props.data.wsdata[this.state.sheetIndex][0]}
              selectedIndex={sheetMap[accCol]}
              onChange={this.onMapChange}
            />
          </td>
        </tr>
      );
    });

    const {
      messages,
      isImportButtonDisabled,
      duplicatedAccountNums,
    } = this.validation();
    const renderMessages = messages.map((msg, i) => <p key={i}>{msg}</p>);

    return (
      <div className="ImportAccountsModal">
        <h2>Import accounts</h2>
        <p>Select sheet:</p>
        <select onChange={this.onSheetChange}>{worksheets}</select>
        <p>Map columns:</p>
        <table>
          <tbody>
            <tr key={-1}>
              <th>Account field</th>
              <th>Sheet Column</th>
            </tr>
            {mapSelectors}
          </tbody>
        </table>
        {duplicatedAccountNums.length > 0 && (
          <div>
            <br />
            Choose update mode for accounts with already existing numbers but
            different description in Excel data:
            <br />
            <br />
            <input
              type="radio"
              id="addAccounts"
              name="accountsUpdateMode"
              value={ACCOUNT_UPDATE_MODE.ADD_ACCOUNTS}
              onChange={this.handleInputChange}
              checked={accountsUpdateMode === ACCOUNT_UPDATE_MODE.ADD_ACCOUNTS}
            />
            <label htmlFor="addAccounts">Add as new accounts</label>
            <br />
            <input
              type="radio"
              id="updateAccounts"
              name="accountsUpdateMode"
              value={ACCOUNT_UPDATE_MODE.UPDATE_ACCOUNTS}
              onChange={this.handleInputChange}
              checked={
                accountsUpdateMode === ACCOUNT_UPDATE_MODE.UPDATE_ACCOUNTS
              }
            />
            <label htmlFor="updateAccounts">Update existing accounts</label>
          </div>
        )}
        <div className="messages">{renderMessages}</div>
        <form onSubmit={this.handleSubmit}>
          <input
            type="submit"
            value="Import"
            className="btn"
            disabled={isImportButtonDisabled}
          />
        </form>
      </div>
    );
  }
}

export default connect(
  (state) => ({
    accounts: state.accounts,
  }),
  {
    importAccounts,
    importAccountsWithAdding,
    addImport,
  }
)(ImportAccountsModal);
